import { BrowserStorageAdapter } from "./browser-storage-adapter";
import { compressStringToBase64, decompressFromBase64 } from "./compression";

const tenMinutes = 1000 * 60 * 10;
const dontClearBelowNumOfEntries = 10;

/**
 * Extends the browser storage, which just json serializes the data by gzip compression.
 * LocalStorage, which is usually used as a backend at limits the amount of stored data
 * to about 5mb.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Storage_API/Storage_quotas_and_eviction_criteria#how_much_data_can_be_stored
 *
 * In some cases the stored data can exceed these limits. This where compression comes in very handy.
 * In one test case, it reduces 6.2mb raw data to only 0.36mb of compressed data which prevents the
 * storage from failing.
 */
export class BrowserStorageAdapterCompressed extends BrowserStorageAdapter {
  private lastCacheClear = Date.now();
  private compressionCache: { [key: string]: { compressedValue: string; rawValue: string } | undefined } = {};
  private compressionController: { [key: string]: AbortController | undefined } = {};
  private keyLastInteractedWith: { [key: string]: number | undefined } = {};

  private abortRunningCompressionDueToNewValueAndGetNewSignal(key: string): AbortSignal {
    /**
     * As the compression is async, while the rest of the code (setting, subscribing)
     * is sync due to how react useSyncExternal store needs it, it is possible
     * that compression/decompression with stale values from previous calls
     * are still running. Aborting them is more efficient, than keeping them running.
     *
     * But way more important is, that due to them being async it is possible that
     * an older value finished (de)compressing after a newer one. Aborting still
     * running compressions and also checking if it possibly was aborted, after
     * already returning a value, helps not to write or return stale values.
     */
    this.compressionController[key]?.abort("stale, got new value already");
    const controller = new AbortController();
    this.compressionController[key] = controller;
    return controller.signal;
  }

  /**
   * Otherwise unused caches leak memory/never get cleared, which might be problematic
   * as this is explicitely made for larger data entries.
   */
  private clearLikelyUnusedCaches() {
    if (Date.now() - this.lastCacheClear < tenMinutes) return;
    if (Object.keys(this.compressionCache).length < dontClearBelowNumOfEntries) return;

    for (const key of Object.keys(this.compressionCache)) {
      const lastInteraction = this.keyLastInteractedWith[key] ?? 0;
      if (Date.now() - lastInteraction > tenMinutes) {
        delete this.compressionCache[key];
        delete this.compressionController[key];
        delete this.keyLastInteractedWith[key];
      }
    }

    for (const key of Object.keys(this.compressionController)) {
      const lastInteraction = this.keyLastInteractedWith[key] ?? 0;
      if (Date.now() - lastInteraction > tenMinutes) {
        delete this.compressionCache[key];
        delete this.compressionController[key];
        delete this.keyLastInteractedWith[key];
      }
    }

    this.lastCacheClear = Date.now();
  }

  subscribe(key: string): (callback: () => void) => () => void {
    this.keyLastInteractedWith[key] = Date.now();
    return super.subscribe(key);
  }

  getSnapshot(key: string) {
    this.keyLastInteractedWith[key] = Date.now();

    const compressedValue = super.getSnapshot(key);
    if (typeof compressedValue !== "string") return compressedValue;

    if (compressedValue !== this.compressionCache[key]?.compressedValue) {
      const signal = this.abortRunningCompressionDueToNewValueAndGetNewSignal(key);

      decompressFromBase64(compressedValue, { signal })
        .then(rawValue => {
          // This prevents stale values from being written to storage
          if (signal.aborted) return;

          this.compressionCache[key] = {
            rawValue: rawValue,
            compressedValue,
          };

          // Due to the async nature of the decompressing, but react requiring a sync response,
          // we have to return stale values, via the cache to satisfy react.
          // Dispatching another storage event here after updating the cache will make react
          // call the getSnapshot function again, to retrieve this now updated value.
          this.dispatchStorageEvent(key, compressedValue);
        })
        .catch(err => {
          // This prevents stale values from being written to storage
          if (signal.aborted) return;

          console.error("failed decompressing data, clearing storage", err);
          // Clear storage as data is likely corrupted
          this.removeItem(key);
        });
    }

    this.clearLikelyUnusedCaches();
    return this.compressionCache[key]?.rawValue;
  }

  removeItem(key: string) {
    this.keyLastInteractedWith[key] = Date.now();

    delete this.compressionCache[key];
    super.removeItem(key);
  }

  setItem(key: string, stringValue: string) {
    this.keyLastInteractedWith[key] = Date.now();

    const signal = this.abortRunningCompressionDueToNewValueAndGetNewSignal(key);
    compressStringToBase64(stringValue, { signal }).then(compressedValue => {
      this.compressionCache[key] = {
        rawValue: stringValue,
        compressedValue,
      };

      // this prevents stale values from being written to storage
      if (!signal.aborted) {
        super.setItem(key, compressedValue);
      }
    });
  }
}
